/* eslint-disable react/button-has-type,react/prop-types */
import type { ButtonHTMLAttributes } from 'react';
import { merge } from 'lodash-es';
import React from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type { VariantProp } from '../../common/colors';
import type { BadgeSize } from '../../components/Badge/Badge';
import type { ShortcutKey } from '../../formatting/Shortcut/Shortcut';
import type { CSS } from '../../stitches.config';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { PolymorphicComponentProps } from '../../utilities/types/polymorphicAsProp';
import type { CalloutUnderlineProp } from '../Callout/Callout';
import type {
  ControlAppearance,
  ControlCondense,
  ControlMaxWidth,
  ControlMinWidth,
  ControlSize,
  ControlVariantInput,
  ControlWidth,
} from '../shared/types';
import { Icon } from '../../assets/Icon/Icon';
import { LoadingIcon } from '../../assets/LoadingIcon/LoadingIcon';
import { atContainers, containers } from '../../common/containers';
import { Badge } from '../../components/Badge/Badge';
import {
  type TooltipSide,
  Tooltip,
  useInsideTooltipTrigger,
} from '../../components/Tooltip/Tooltip';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import Shortcut, { shortcutsVisible } from '../../formatting/Shortcut/Shortcut';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { AlignStack, AlignStackElements } from '../../utilities/AlignStack/AlignStack';
import { getCanHover } from '../../utilities/getCanHover';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import { VStack } from '../../utilities/Stack/VStack';
import {
  AccessLevel,
  isAccessLevelVisible,
  useAccessLevelContext,
} from '../../utilities/useAccessLevelContext';
import { CalloutUnderline, useCalloutDescription } from '../Callout/Callout';
import {
  useControlAppearance,
  useControlCondense,
  useControlGhost,
  useControlMaxWidth,
  useControlMinWidth,
  useControlSize,
  useControlVariant,
  useControlWidth,
} from '../shared/context';
import { selectors, transitions } from '../shared/styles';
import {
  controlArrowsSize,
  controlBorderRadius,
  controlGap,
  controlHeight,
  controlIconSize,
  controlLoadingSize,
  controlPaddingX,
  controlPaddingY,
  controlVars,
  getControlPreset,
} from '../shared/vars';
import {
  getControlVariantCompoundVariants,
  getInteriorVariantStyles,
  iconColor,
  internalDisabledStyles,
  internalEnabledStyles,
  labelColor,
  loadingIconColor,
} from './Button.styles';

const ButtonIcon = styled(Icon, {
  opacity: 1,
  size: controlIconSize,
  color: iconColor,
  transition: transitions.control,

  variants: {
    isLoading: {
      true: {
        opacity: 0,
      },
      false: {},
    },
  },
});
ButtonIcon.displayName = 'ButtonIcon';

const ButtonMenuArrow = styled(ButtonIcon, {
  size: controlArrowsSize,
});
ButtonMenuArrow.displayName = 'ButtonMenuArrow';

const ButtonShortcut = styled(Shortcut, {
  transition: transitions.control,

  variants: {
    isLoading: {
      true: {
        opacity: 0,
      },
      false: {},
    },
  },
});
ButtonShortcut.displayName = 'ButtonShortcut';

const ButtonLoadingIcon = styled(LoadingIcon, {
  size: controlLoadingSize,
  fill: loadingIconColor,
  transition: transitions.control,

  circle: {
    fill: loadingIconColor,
  },
});
ButtonLoadingIcon.displayName = 'ButtonLoadingIcon';

const ButtonLabel = styled(Text, {
  display: 'flex',
  color: labelColor,
  fontWeight: '$bold',
  transition: transitions.control,

  variants: {
    isLoading: {
      true: {
        opacity: 0,
      },
      false: {
        opacity: 1,
      },
    },
    arrangement: {
      'leading-icon': {},
      'leading-label': {},
    },
    swapForShortLabel: {
      true: {
        [containers.application.notWide]: {
          display: 'none',
        },
      },
      false: {},
    },
    stretch: {
      true: {
        width: '100%',
      },
      false: {},
    },
  },
});
ButtonLabel.displayName = 'ButtonLabel';

const ButtonShortLabel = styled(ButtonLabel, {
  display: 'none',

  [containers.application.notWide]: {
    display: 'flex',
  },
});
ButtonShortLabel.displayName = 'ButtonShortLabel';

const hiddenLabelMinWidth: Record<ControlSize, string> = {
  'x-small': '$24',
  small: '$28',
  medium: '$32',
  large: '$40',
  'x-large': '$48',
};

export const ButtonContainer = styled('button', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: controlGap,
  heightAll: controlHeight,
  minWidth: 'min-content',
  paddingX: controlPaddingX,
  paddingY: controlPaddingY,
  borderRadius: controlBorderRadius,
  opacity: 1,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
  transition: transitions.control,

  '&&:hover': {
    textDecoration: 'none',
  },

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    appearance: {
      muted: {},
      vivid: {},
    },
    arrangement: {
      'hidden-label': {},
      'leading-icon': {},
      'leading-label': {},
    },
    hasMenu: {
      true: {},
      false: {},
    },
    ...controlVars,
    truncate: {
      true: {
        minWidth: 0,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      },
      false: {},
    },
    internal: {
      true: {},
      false: {},
    },
    isDisabled: {
      true: {
        cursor: 'not-allowed',
        opacity: 0.5,
      },
      false: {},
    },
    ghost: {
      true: {},
      false: {},
    },
    invalid: {
      true: {},
      false: {},
    },
    variant: {
      alternative: {},
      attention: {},
      brand: {},
      negative: {},
      neutral: {},
      positive: {},
    },
  },
  compoundVariants: [
    // Icon only
    {
      arrangement: 'hidden-label',
      size: 'x-small',
      hasMenu: false,
      css: {
        minWidth: hiddenLabelMinWidth['x-small'],
      },
    },
    {
      arrangement: 'hidden-label',
      size: 'small',
      hasMenu: false,
      css: {
        minWidth: hiddenLabelMinWidth.small,
      },
    },
    {
      arrangement: 'hidden-label',
      size: 'medium',
      hasMenu: false,
      css: {
        minWidth: hiddenLabelMinWidth.medium,
      },
    },
    {
      arrangement: 'hidden-label',
      size: 'large',
      hasMenu: false,
      css: {
        minWidth: hiddenLabelMinWidth.large,
      },
    },
    {
      arrangement: 'hidden-label',
      size: 'x-large',
      hasMenu: false,
      css: {
        minWidth: hiddenLabelMinWidth['x-large'],
      },
    },
    ...getControlVariantCompoundVariants({ useSubmitSelector: true }),

    // Internal
    {
      internal: true,
      isDisabled: false,
      css: internalEnabledStyles,
    },
    {
      internal: true,
      isDisabled: true,
      css: internalDisabledStyles,
    },

    {
      isDisabled: false,
      invalid: true,
      css: {
        boxShadow: shadows.fieldErrorLight,

        '&:hover:not(:focus)': {
          boxShadow: shadows.fieldErrorLight,
        },

        [darkThemeSelector]: {
          boxShadow: shadows.fieldErrorDark,

          '&:hover:not(:focus)': {
            boxShadow: shadows.fieldErrorDark,
          },
        },
      },
    },

    // Ghost
    {
      ghost: true,
      css: {
        '&[data-ghost="true"]:not(:hover, :focus, :focus-visible, :focus-within)': {
          backgroundColor: colors.transparent,
          boxShadow: colors.transparent,
          outlineColor: colors.transparent,
        },
      },
    },
  ],
});
ButtonContainer.displayName = 'ButtonContainer';

const ButtonLoadingContainer = styled('div', {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
ButtonLoadingContainer.displayName = 'ButtonLoadingContainer';

// Offset from the centre by half the label's line height, so the bar sits just under the contents
// at every control height. x-small has no room for that, so it borrows a pixel from the label.
const ButtonCallout = styled(CalloutUnderline, {
  left: controlPaddingX,
  right: controlPaddingX,

  variants: {
    size: {
      'x-small': { top: 'calc(50% + 7px)' },
      small: { top: 'calc(50% + $space$8)' },
      medium: { top: 'calc(50% + $space$10)' },
      large: { top: 'calc(50% + $space$12)' },
      'x-large': { top: 'calc(50% + $space$12)' },
    },
    isLoading: {
      true: {
        opacity: 0,
      },
      false: {},
    },
    condensedOnly: {
      true: {
        display: 'none',
      },
      false: {},
    },
  },
});
ButtonCallout.displayName = 'ButtonCallout';

export type ButtonCalloutProp = CalloutUnderlineProp & {
  /** Only underline while `condense` hides the label, for when the label already says as much. */
  condensedOnly?: boolean;
};

export type ButtonAppearanceProp = ControlAppearance;
export type ButtonArrangementProp = 'hidden-label' | 'leading-icon' | 'leading-label';
export type ButtonMenuArrowProp = 'dropdown' | 'select' | 'external';

const menuArrowIcons: Record<ButtonMenuArrowProp, IconName> = {
  dropdown: 'chevron-down',
  select: 'chevrons-vertical',
  external: 'arrow-up-right',
};

export type ButtonLabelAlignProp = 'start' | 'center' | 'end';
export type ButtonSizeProp = ControlSize;
export type ButtonTypeProp = 'button' | 'submit' | 'reset';
export type ButtonFixturesProp = VariantProp;
export type ButtonVariantProp = ControlVariantInput;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Additional annotation such a result count within the button.
   * */
  annotation?: React.ReactNode;
  /**
   * The level required to access the button.
   */
  accessLevel?: AccessLevel;
  /**
   * Determines if the button is muted (more flat, simple colors) or vivid (more pronounced, colorful).
   */
  appearance?: ButtonAppearanceProp;
  /**
   * Determines the layout of the button's content.
   */
  arrangement?: ButtonArrangementProp;
  /**
   * Underlines the button's contents to mark it as in use, such as a filter with a value applied or
   * unsaved changes behind it. The label describes the button to assistive technology and is
   * appended to the tooltip of icon-only buttons.
   */
  callout?: ButtonCalloutProp;
  /**
   * The content of the button.
   */
  children: React.ReactNode;
  /**
   * Hides the button's label while a container query (`containers.*`) matches. Pass queries
   * directly or via `ControlGroup`; `true` uses the default (application below desktop width).
   */
  condense?: boolean | ControlCondense;
  /**
   * Disables the button.
   */
  disabled?: boolean;
  /**
   * Marks the button as invalid.
   */
  invalid?: boolean;
  /**
   * Set the form attribute.
   */
  form?: string;
  /**
   * Set which icon to display, no value displays no icon.
   */
  icon?: IconName;
  /**
   * Marks the button as internal.
   */
  internal?: boolean;
  /**
   * Sets the variant for the button's internal fixtures (icon, label, etc.) when `variant` is `neutral`.
   */
  fixtures?: ButtonFixturesProp;
  /**
   * Hides the button's background and stroke until interacted with.
   */
  ghost?: boolean;
  /**
   * Displays a trailing arrow: a dropdown or select arrow for menus, or an external arrow for links
   * that take the user somewhere else.
   */
  menuArrow?: ButtonMenuArrowProp;
  /**
   * Set the alignment of the button's label.
   */
  labelAlign?: ButtonLabelAlignProp;
  /**
   * Stretches the button's label to fill the width of the button.
   */
  labelStretch?: boolean;
  /**
   * Displays a loading icon.
   */
  loading?: boolean;
  /**
   * Set shortcut keys for the button.
   */
  shortcut?: ShortcutKey[];
  /**
   * Shorter label for the button.
   */
  shortLabel?: React.ReactNode;
  /**
   * Set the size of the button.
   */
  size?: ButtonSizeProp;
  /**
   * Truncate the button label.
   */
  truncate?: boolean;
  /**
   * If the button label is shown as a tooltip, the preferred side of the trigger to render against when open.
   */
  tooltipSide?: TooltipSide;
  /**
   * Set the type of the button.
   */
  type?: ButtonTypeProp;
  /**
   * Set the most appropriate variant of the component for your use.
   */
  variant?: ButtonVariantProp;
  /**
   * Set a width.
   */
  width?: ControlWidth;
  /**
   * Set a maximum width.
   */
  maxWidth?: ControlMaxWidth;
  /**
   * Set a minimum width.
   */
  minWidth?: ControlMinWidth;
}

function getButtonGap(size: ButtonSizeProp, canHover: boolean): SpacingScale | undefined {
  switch (size) {
    case 'x-small':
      return 2;
    case 'small':
    case 'medium':
      return canHover ? 4 : 6;
    case 'large':
      return 6;
    case 'x-large':
      return 8;
  }

  return undefined;
}

function getShortcutVariant(variant: VariantProp): 'destructive' | 'neutral' | 'primary' {
  if (variant === 'negative') return 'destructive';
  if (variant === 'neutral') return 'neutral';
  return 'primary';
}

export function Button<Tag extends React.ElementType>({
  annotation,
  appearance,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  arrangement = 'leading-label',
  callout,
  children,
  condense,
  disabled = false,
  ghost,
  icon,
  accessLevel: accessLevelProp,
  internal,
  fixtures,
  invalid = false,
  labelAlign,
  labelStretch = false,
  loading = false,
  maxWidth,
  menuArrow,
  minWidth,
  shortcut,
  shortLabel,
  size,
  truncate = false,
  tooltipSide,
  type = 'button',
  variant,
  width,
  ref,
  ...rest
}: PolymorphicComponentProps<Tag, ButtonProps>) {
  const canHover = getCanHover() ?? true;
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const insideTooltipTrigger = useInsideTooltipTrigger();
  const labelRef = React.useRef<HTMLSpanElement>(null);
  const calloutDescription = useCalloutDescription(callout, ariaDescribedBy);

  const controlSize = useControlSize(size, 'medium');
  const controlAppearance = useControlAppearance(appearance, 'vivid');
  const controlCondense = useControlCondense(condense);
  const controlGhost = useControlGhost(ghost, false);
  const controlWidth = useControlWidth(width);
  const controlMaxWidth = useControlMaxWidth(maxWidth);
  const controlMinWidth = useControlMinWidth(minWidth);
  const controlVariant = useControlVariant(variant, 'brand');

  // Condense inherited from a `ControlGroup` only applies when there's an icon to fall back to.
  const resolvedCondense = condense === undefined && !icon ? false : controlCondense;
  const condenseQueries = resolvedCondense === false ? undefined : resolvedCondense;

  const controlPreset = getControlPreset(controlSize);

  let generalSize;
  if (controlSize === 'x-large') {
    generalSize = 'large';
  } else if (controlSize === 'large') {
    generalSize = 'medium';
  } else if (controlSize === 'medium') {
    generalSize = 'small';
  } else {
    generalSize = 'x-small';
  }

  // Disable the button during its loading state
  const isDisabled = disabled || loading;

  const hiddenLabel = arrangement === 'hidden-label';

  const shortcutRender = shortcut && shortcutsVisible && arrangement !== 'hidden-label' && (
    <ButtonShortcut
      control={controlAppearance !== 'muted'}
      internal={internal}
      isLoading={loading}
      variant={getShortcutVariant(controlVariant)}
      keys={shortcut}
    />
  );

  const fixturesCSS: CSS | undefined =
    fixtures && controlVariant === 'neutral' ? getInteriorVariantStyles(fixtures) : undefined;
  // Hide the label's `AlignStack` wrapper, not just the label, so no flex gap is left behind.
  const condenseCSS: CSS | undefined =
    condenseQueries && arrangement !== 'hidden-label'
      ? atContainers(condenseQueries, {
          [`& ${AlignStackElements}:has(> ${ButtonLabel})`]: { display: 'none' },
          [`& ${ButtonCallout}`]: { display: 'block' },
          ...(menuArrow ? undefined : { minWidth: hiddenLabelMinWidth[controlSize] }),
        })
      : undefined;
  const css = merge(
    fixturesCSS,
    widthCSS(controlWidth),
    maxWidthCSS(controlMaxWidth),
    minWidthCSS(controlMinWidth),
    condenseCSS,
  );

  const buttonRender = (
    <ButtonContainer
      {...rest}
      css={css}
      type={type}
      ref={ref}
      aria-label={
        ariaLabel ||
        (arrangement === 'hidden-label' || resolvedCondense !== false ? `${children}` : undefined)
      }
      aria-describedby={calloutDescription['aria-describedby']}
      data-ghost={controlGhost}
      appearance={controlGhost ? 'muted' : controlAppearance}
      arrangement={arrangement}
      variant={controlVariant}
      size={controlSize}
      internal={internal || accessLevel === AccessLevel.Internal}
      ghost={controlGhost}
      isDisabled={isDisabled}
      hasMenu={Boolean(menuArrow)}
      disabled={isDisabled}
      invalid={invalid}
      truncate={truncate}
    >
      {loading && (
        <ButtonLoadingContainer>
          <ButtonLoadingIcon />
        </ButtonLoadingContainer>
      )}
      {callout != null && (
        <ButtonCallout
          condensedOnly={Boolean(callout.condensedOnly) && !hiddenLabel}
          isLoading={loading}
          size={controlSize}
          variant={callout.variant}
        />
      )}
      {calloutDescription.description}
      <AlignStack
        direction="row"
        justify="center"
        preset={controlPreset}
        end={
          (shortcutRender || menuArrow) && (
            <>
              {shortcutRender}
              {menuArrow && (
                <ButtonMenuArrow isLoading={loading} icon={menuArrowIcons[menuArrow]} />
              )}
            </>
          )
        }
        width="full"
        gap={getButtonGap(controlSize, canHover)}
      >
        <AlignStack
          direction={
            arrangement === 'leading-icon' || arrangement === 'hidden-label' ? 'row' : 'row-reverse'
          }
          align="center"
          justify="center"
          preset={controlPreset}
          start={icon && <ButtonIcon isLoading={loading} icon={icon} />}
          end={
            Boolean((accessLevel && isAccessLevelVisible(accessLevel)) || annotation) && (
              <>
                {accessLevel && isAccessLevelVisible(accessLevel) && (
                  <FeatureBadge size="x-small" arrangement="icon-only" type={accessLevel} />
                )}
                {annotation && (
                  <Badge
                    internal={internal}
                    size={generalSize as BadgeSize}
                    variant={controlVariant}
                    elide={!hiddenLabel}
                  >
                    {annotation}
                  </Badge>
                )}
              </>
            )
          }
          width="full"
          gap={getButtonGap(controlSize, canHover)}
        >
          {!hiddenLabel && (
            <>
              <ButtonLabel
                ref={labelRef}
                isLoading={loading}
                stretch={labelStretch}
                swapForShortLabel={shortLabel != null}
              >
                {children}
              </ButtonLabel>
              {shortLabel != null && (
                <ButtonShortLabel isLoading={loading} stretch={labelStretch}>
                  {shortLabel}
                </ButtonShortLabel>
              )}
            </>
          )}
        </AlignStack>
      </AlignStack>
    </ButtonContainer>
  );

  // Only open the label tooltip while CSS has the label (and its wrapper) hidden.
  const shouldOpenCondenseTooltip = condenseQueries
    ? () => labelRef.current == null || labelRef.current.parentElement?.offsetParent == null
    : undefined;

  if (!insideTooltipTrigger && (hiddenLabel || condenseQueries !== undefined)) {
    return (
      <Tooltip
        contents={
          <VStack spacing={2}>
            <AlignStack
              align="center"
              gap={4}
              wrap="nowrap"
              preset="small"
              end={shortcut && shortcutsVisible && <Shortcut keys={shortcut} />}
            >
              <Text>{children}</Text>
            </AlignStack>
            {callout != null && <Text>{callout.label}</Text>}
          </VStack>
        }
        shouldOpen={shouldOpenCondenseTooltip}
        side={tooltipSide}
        sideOffset={2}
        toggle={false}
      >
        {buttonRender}
      </Tooltip>
    );
  }

  return buttonRender;
}
